import React, { useState, useCallback, useRef, useMemo } from "react";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import toast from 'react-hot-toast';

const libraries = ["places"];
const mapContainerStyle = { 
  width: "100%", 
  height: "400px", 
  borderRadius: "12px",
  border: "1px solid #e5e7eb"
};
const defaultCenter = { lat: 26.9124, lng: 75.7873 }; // Jaipur default

const LocationPicker = ({ onLocationSelect, initialLocation = null }) => {
  // Memoize the load script configuration to prevent reloads
  const loadScriptConfig = useMemo(() => ({
    googleMapsApiKey: "AIzaSyBB7eCnq1g88yxps20vrHfiW3QC07G3LPU",
    libraries,
  }), []);

  const { isLoaded, loadError } = useLoadScript(loadScriptConfig);

  // Debug logging
  console.log('LocationPicker - isLoaded:', isLoaded);
  console.log('LocationPicker - loadError:', loadError);
  console.log('LocationPicker - Google Maps API Key:', "AIzaSyBB7eCnq1g88yxps20vrHfiW3QC07G3LPU");

  const [markerPosition, setMarkerPosition] = useState(
    initialLocation ? { lat: initialLocation.lat, lng: initialLocation.lng } : defaultCenter
  );
  const [address, setAddress] = useState(initialLocation?.address || "");
  const [isLoading, setIsLoading] = useState(false);
  const [mapTimeout, setMapTimeout] = useState(false);
  const mapRef = useRef();

  // Timeout mechanism to detect if map is taking too long to load
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isLoaded && !loadError) {
        console.warn('Google Maps taking too long to load, showing timeout message');
        setMapTimeout(true);
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeout);
  }, [isLoaded, loadError]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    
    // Try to get user's current GPS location
    if (navigator.geolocation) {
      setIsLoading(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const current = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setMarkerPosition(current);
          map.panTo(current);
          map.setZoom(18); // Zoom in for better accuracy
          reverseGeocode(current);
          setIsLoading(false);
          toast.success('Location detected! Pin moved to your current location.');
        },
        (error) => {
          console.log("Location permission denied or error:", error);
          setIsLoading(false);
          toast.error('Could not detect your location. Please click on the map to select your address.');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        }
      );
    } else {
      toast.error('Geolocation is not supported by this browser.');
    }
  }, []);

  const reverseGeocode = (latlng) => {
    if (!window.google || !window.google.maps) return;
    
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: latlng }, (results, status) => {
      if (status === "OK" && results[0]) {
        const formattedAddress = results[0].formatted_address;
        setAddress(formattedAddress);
        
        if (onLocationSelect) {
          onLocationSelect({
            address: formattedAddress,
            lat: latlng.lat,
            lng: latlng.lng,
            coordinates: { lat: latlng.lat, lng: latlng.lng }
          });
        }
        
        toast.success('Address detected from pin location!');
      } else {
        console.error('Reverse geocoding failed:', status);
        toast.error('Could not detect address. Please enter manually.');
      }
    });
  };

  const handleMarkerDragEnd = (e) => {
    const newPos = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };
    setMarkerPosition(newPos);
    reverseGeocode(newPos);
  };

  const handleMapClick = (e) => {
    const clickedPos = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };
    setMarkerPosition(clickedPos);
    reverseGeocode(clickedPos);
  };

  if (loadError) {
    console.error('Google Maps load error:', loadError);
    return (
      <div style={{
        width: "100%",
        height: "400px",
        backgroundColor: "#f3f4f6",
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "16px",
        border: "1px solid #e5e7eb"
      }}>
        <div style={{ fontSize: "18px", fontWeight: "600", color: "#ef4444" }}>
          Error loading Google Maps
        </div>
        <div style={{ fontSize: "14px", color: "#6b7280", textAlign: "center", marginBottom: "16px" }}>
          {loadError.message || "Please check your internet connection and try again"}
        </div>
        
        {/* Fallback Map using iframe */}
        <div style={{
          width: "100%",
          height: "300px",
          borderRadius: "8px",
          overflow: "hidden",
          border: "1px solid #d1d5db"
        }}>
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3557.123456789!2d75.7873!3d26.9124!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x396c4adf4c57e281%3A0xce1c63a0cf22e09!2sJaipur%2C%20Rajasthan!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Jaipur Map"
          />
        </div>
        
        <div style={{ fontSize: "12px", color: "#6b7280", textAlign: "center" }}>
          Using fallback map. Please enter your address manually below.
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div style={{
        width: "100%",
        height: "400px",
        backgroundColor: "#f3f4f6",
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "16px",
        border: "1px solid #e5e7eb"
      }}>
        <div style={{
          width: "40px",
          height: "40px",
          border: "4px solid #f3f4f6",
          borderTop: "4px solid #ff6b35",
          borderRadius: "50%",
          animation: "spin 1s linear infinite"
        }}></div>
        <div style={{ fontSize: "16px", fontWeight: "600", color: "#374151" }}>
          {mapTimeout ? "Map loading is taking longer than expected..." : "Loading map..."}
        </div>
        {mapTimeout && (
          <div style={{ fontSize: "14px", color: "#6b7280", textAlign: "center" }}>
            This might be due to API key issues or network problems.
            <br />
            Please check the browser console for errors.
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      {/* Instructions Overlay */}
      <div style={{
        position: "absolute",
        top: "16px",
        left: "16px",
        right: "16px",
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        color: "white",
        padding: "12px 16px",
        borderRadius: "8px",
        fontSize: "14px",
        fontWeight: "600",
        textAlign: "center",
        backdropFilter: "blur(10px)",
        zIndex: 1000
      }}>
        📍 Click anywhere on the map to place your pin
      </div>

      {/* Current Location Button */}
      <button
        onClick={() => {
          if (navigator.geolocation) {
            setIsLoading(true);
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                const current = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                setMarkerPosition(current);
                mapRef.current?.panTo(current);
                mapRef.current?.setZoom(18);
                reverseGeocode(current);
                setIsLoading(false);
                toast.success('Location detected! Pin moved to your current location.');
              },
              (error) => {
                setIsLoading(false);
                toast.error('Could not detect your location.');
              }
            );
          }
        }}
        disabled={isLoading}
        style={{
          position: "absolute",
          bottom: "16px",
          right: "16px",
          width: "48px",
          height: "48px",
          backgroundColor: "white",
          border: "2px solid #e5e7eb",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: isLoading ? "not-allowed" : "pointer",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 1000,
          opacity: isLoading ? 0.7 : 1,
          transition: "all 0.2s ease"
        }}
        onMouseEnter={(e) => {
          if (!isLoading) {
            e.currentTarget.style.borderColor = "#ff6b35";
            e.currentTarget.style.transform = "scale(1.05)";
          }
        }}
        onMouseLeave={(e) => {
          if (!isLoading) {
            e.currentTarget.style.borderColor = "#e5e7eb";
            e.currentTarget.style.transform = "scale(1)";
          }
        }}
      >
        {isLoading ? (
          <div style={{
            width: "20px",
            height: "20px",
            border: "2px solid #f3f4f6",
            borderTop: "2px solid #ff6b35",
            borderRadius: "50%",
            animation: "spin 1s linear infinite"
          }}></div>
        ) : (
          <div style={{
            width: "20px",
            height: "20px",
            backgroundColor: "#ff6b35",
            borderRadius: "50%",
            position: "relative"
          }}>
            <div style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "8px",
              height: "8px",
              backgroundColor: "white",
              borderRadius: "50%"
            }}></div>
          </div>
        )}
      </button>

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={15}
        center={markerPosition}
        onLoad={onMapLoad}
        onClick={handleMapClick}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          zoomControl: true,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        }}
      >
        <Marker
          position={markerPosition}
          draggable
          onDragEnd={handleMarkerDragEnd}
          icon={{
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="18" fill="#ff6b35" stroke="white" stroke-width="4"/>
                <circle cx="20" cy="20" r="8" fill="white"/>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(40, 40),
            anchor: new window.google.maps.Point(20, 20)
          }}
        />
      </GoogleMap>

      {/* Address Display */}
      <div style={{ marginTop: "16px" }}>
        <label style={{
          display: "block",
          fontSize: "14px",
          fontWeight: "600",
          color: "#374151",
          marginBottom: "8px"
        }}>
          Detected Address:
        </label>
        <input
          type="text"
          value={address}
          readOnly
          placeholder="Click on the map to detect address..."
          style={{
            width: "100%",
            padding: "12px 16px",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            fontSize: "14px",
            backgroundColor: "#f9fafb",
            color: "#374151"
          }}
        />
      </div>
    </div>
  );
};

export default LocationPicker;
